<script>
import moment from "moment";
import { componentStore } from "@common/componentStore/componentStore";
import { configStore } from "@common/configStore";
import { Query } from '@syncfusion/ej2-data';

const unixDates = configStore.state.cfg.unixDates;
const dateTypeOptions = [
    { text: 'Equals', value: 'equal' },
    { text: 'Is Before', value: 'before' },
    { text: 'Is After', value: 'after' },
    { text: 'Is Between', value: 'between' },
    {
        text: 'Today',
        value: 'today',
        date1: unixDates.startOfToday,
        date2: unixDates.endOfToday
    },
    {
        text: 'Yesterday',
        value: 'yesterday',
        date1: unixDates.startOfYesterday,
        date2: unixDates.endOfYesterday
    },
    {
        text: 'This Week',
        value: 'thisweek',
        date1: unixDates.startOfWeek,
        date2: unixDates.endOfWeek
    },
    {
        text: 'Last Week',
        value: 'lastweek',
        date1: unixDates.startOfLastWeek,
        date2: unixDates.endOfLastWeek
    },
    {
        text: 'This Month',
        value: 'thismonth',
        date1: unixDates.startOfMonth,
        date2: unixDates.endOfMonth
    },
    {
        text: 'Last Month',
        value: 'lastmonth',
        date1: unixDates.startOfLastMonth,
        date2: unixDates.endOfLastMonth
    },
    {
        text: 'This Year',
        value: 'thisyear',
        date1: unixDates.startOfYear,
        date2: unixDates.endOfYear
    },
    {
        text: 'Last Year',
        value: 'lastyear',
        date1: unixDates.startOfLastYear,
        date2: unixDates.endOfLastYear
    },
];
const isRelativeDate = (dateType) => {
    const nonRelativeDates = ['equal', 'between', 'before', 'after'];
    return nonRelativeDates.indexOf(dateType) === -1;
};

const condensedFilterConfig = {
    cId: "condensedFilter",
    cType: "condensedFilter",
    cTitle: "Condensed Filter",
    cTemplate: `<div class="condensed-filter">
                    <div v-if="!hasFilterOptions">Filter options not provided.</div>
                    <div v-else>
                        <div class="filter-controls">
                            <div class="search-button">
                                <control name="FILTER_TYPE" :items="fOptions" :value="filterProp" @update:value="filterProp = $event"></control>
                                <control name="FILTER_DATE_TYPE" :value="dateTypes[filterProp]" @update:value="dateTypes[filterProp] = $event" v-if="filterType==='date'"></control>
                            </div>
                            <div class="search-fields">
                                <div v-if="filterType==='date' && dateType !== 'between'" class="control-spacer"></div>
                                <div v-if="filterType==='date' && dateType !== 'equal' && dateType !== 'before' && dateType !== 'after' && dateType !== 'between'" class="control-spacer date-msg"><span>{{dateTypeMsg}}</span></div>
                                <control name="FILTER_LIST" :items="fOptions" v-if="filterType!=='date' || dateType === 'equal' || dateType === 'before' || dateType === 'after' || dateType === 'between'"></control>
                                <span class="e-icons e-search">{{}}</span>
                            </div>
                        </div>
                        <div class="filter-status">
                            <div class="result-summary">
                                <div class="filter-status-counter">
                                    <span v-if="!filterItems || !filterItemsLength">No {{computedItemLabel}} to search for.</span>
                                    <span v-else-if="firstFilterDelay">Loading... Please wait.</span>
                                    <span v-else-if="filteredItems.length">
                                        <span v-if="filteredItems.length === filterItemsLength">
                                            Showing all {{filterItemsLength}} {{computedItemLabel}}.
                                        </span>
                                        <span v-else>
                                            Showing {{filteredItems.length}} of {{filterItemsLength}} {{computedItemLabel}}.
                                        </span>
                                    </span>
                                    <span v-else>No matching {{computedItemLabel}}</span>
                                </div>
                                <div v-if="!firstFilterDelay && useModifyDateFilter" class="filter-status-unused">
                                    <span>Show unused</span>
                                    <div class="filter-status-unused-switch">
                                        <control name="SWITCH_UNUSED_FILTER"></control>
                                    </div>
                                </div>
                            </div>
                            <div v-if="filtersApplied" class="filter-clear-all">
                                <control name="CLEAR_ALL_FILTERS" :items="filterOptions"></control>
                            </div>
                        </div>
                    </div>
                </div>`,
    controls: [
        {
            name: 'FILTER_LIST',
            type: "list",
            itemContentTemplate: `<div v-show='component.selectedFilter == data.key'>
                <div v-if="data.type==='date'">
                    <control name="FILTER_DATE_INPUT" :items="data.items" :value="component.dateValues[data.key].date1" @update:value="component.dateValues[data.key].date1 = $event" :placeholder='component.data1Placeholder'></control>
                    <control v-if="component.dateTypes[data.key] === 'between'" name="FILTER_DATE_INPUT2" :items="data.items" :value="component.dateValues[data.key].date2" @update:value="component.dateValues[data.key].date2 = $event" :placeholder='component.data2Placeholder'></control>
                </div>
                <div v-else>
                    <control name="FILTER_INPUT" :items="data.items"
                    :config="{
                        placeholder: data.placeholder,
                        allowCustomValue: data.allowCustomValue,
                        allowFiltering: data.allowFiltering,
                        filterType: data.filterType,
                        fields: data.fields,
                        index: data.key, itemTemplate:
                        data.itemTemplate,
                        maximumSelectionLength: data.maximumSelectionLength || 1000}"
                        ></control>
                </div>
                </div>`
        },
        {
            name: 'FILTER_TYPE',
            type: 'dropdownbutton',
            fields: { text: 'label', value: 'key' },
            useSelectedOptionAsLabel: true,
            onselect: 'switchType'
        },
        {
            name: 'SWITCH_UNUSED_FILTER',
            type: 'boolean',
            onchange: 'applyUnusedFilter',
            default: false
        },
        {
            name: 'CLEAR_ALL_FILTERS',
            type: 'button',
            cssClass: 'e-link',
            label: 'reset',
            onclick: 'clearAllFilters'
        },
        {
            name: 'FILTER_DATE_TYPE',
            type: 'dropdownbutton',
            fields: { text: 'text', value: 'value' },
            items: dateTypeOptions,
            useSelectedOptionAsLabel: true,
            onselect: 'filterDate',
        },
        {
            name: 'FILTER_DATE_INPUT',
            type: 'date',
            onchange: 'filter',
        },
        {
            name: 'FILTER_DATE_INPUT2',
            type: 'date',
            onchange: 'filter',
        },
        {
            name: 'FILTER_INPUT',
            type: 'multiselect',
            fields: { text: 'text', value: 'value' },
            mode: 'Box',
            onchange: 'filter',
            onfiltering: 'filteringFnc'
        }
    ]
};

let condensedFilterCmp = {
    name: 'condensedFilter',
    props: {
        filterOptions: {
            default: null
        },
        filterItems: {
            default: null
        },
        result: {
            default: {}
        },
        filtering: {
            default: false
        },
        itemLabel: {
            default: false
        },
        subObjectKey: {
            default: false
        },
        additionalConfig: {
            default: false
        },
        useModifyDateFilter: {
            default: null
        },
        useModifyDateFilterCallback: {
            default: null
        }
    },
    data() {
        return {
            currentFilterItems: [],
            updateOptions: false,
            filteredItems: [],
            selectedFilter: null,
            currentFilterOptions: {},
            formattedFilterOptions: {},
            dateTypes: {},
            dateValues: {},
            filterProp: '',
            filterTimeout: false,
            firstFilterDelay: 1200, // Delays the first filter, then is set to false
            filterDelay: 200,
        };
    },
    computed: {
        filterItemsLength() {
            let notChanged = library.isEqualObj(this.currentFilterItems, this.filterItems);
            if (notChanged) return this.currentFilterItems.length;
            this.currentFilterItems = library.cloneObj(this.filterItems);
            this.updateOptions = true;
            this.filter();
            return this.currentFilterItems.length;
        },
        dateType() {
            return this.dateTypes[this.filterProp];
        },
        dateTypeMsg() {
            if (!this.dateType) return 'Select date type from the left';
            let typeOption = dateTypeOptions.find((option) => option.value === this.dateType);
            if (!typeOption) return 'Select date type from the left';
            return 'Matching ' + typeOption.text.toLowerCase();
        },
        data1Placeholder() {
            if (this.dateType === 'equal') return 'Enter a date to match';
            if (this.dateType === 'before') return 'Enter a maximum date';
            if (this.dateType === 'after') return 'Enter a minimum date';
            return 'Enter a starting date';
        },
        data2Placeholder() {
            return 'Enter an ending date';
        },
        computedItemLabel() {
            return this.itemLabel || 'results';
        },
        filtersApplied() {
            return this.hasFilterOptions && !!Object.values(this.appliedFilters)
                .find((value) => (
                    value.length
                    || (value.date1 && value.date1 !== 'Invalid date')
                    || (value.date2 && value.date2 !== 'Invalid date')
                ));
        },
        hasFilterOptions() {
            return !!this.filterOptions;
        },
        filterType() {
            let fOpts = this.fOptions;
            return fOpts
                && fOpts[this.filterProp]
                && fOpts[this.filterProp].type
                && fOpts[this.filterProp].type.toLowerCase();
        },
        autocompleteItems() {
            let filterOptions = this.filterOptions;
            let filterItems = this.filterItems;
            let items = [];
            let addedItems = [];
            let sKey = this.subObjectKey;
            for (let key in filterItems) {
                if (key === 'length') continue;
                let tmpComponent = sKey ? filterItems[key][sKey] : filterItems[key];
                for (let prop in filterOptions) {
                    if (prop === 'length') continue;
                    let tmpProp = prop;
                    if (filterOptions && filterOptions[prop] && filterOptions[prop].isGroupFor) {
                        tmpProp = filterOptions[prop].isGroupFor;
                    }
                    if (!tmpComponent[tmpProp]) continue;
                    if (typeof addedItems[prop] === 'undefined') addedItems[prop] = [];
                    if (typeof items[prop] === 'undefined') items[prop] = [];
                    let useFieldsSettingsValue = filterOptions && filterOptions[prop] && typeof filterOptions[prop].fields === 'object' && filterOptions[prop].fields.value;
                    if (addedItems[prop].indexOf(tmpComponent[useFieldsSettingsValue || tmpProp]) === -1) {
                        addedItems[prop].push(tmpComponent[useFieldsSettingsValue || tmpProp]);
                        let tmpItem = {
                            text: tmpComponent[tmpProp],
                            value: tmpComponent[useFieldsSettingsValue || tmpProp],
                            key: useFieldsSettingsValue || tmpProp
                        };
                        if (typeof this.additionalConfig === 'object' && this.additionalConfig.additionalValues) {
                            for (let tmpAdditionalValueIdx in this.additionalConfig.additionalValues) {
                                let tmpAdditionalValueName = this.additionalConfig.additionalValues[tmpAdditionalValueIdx];
                                if (typeof tmpComponent[tmpAdditionalValueName] !== 'undefined') {
                                    if (typeof tmpItem.additionalValue === 'undefined') tmpItem.additionalValue = {};
                                    tmpItem.additionalValue[tmpAdditionalValueName] = library.cloneObj(tmpComponent[tmpAdditionalValueName]);
                                }
                            }
                        }
                        if (filterOptions && filterOptions[prop] && filterOptions[prop].isGroupFor) {
                            tmpItem[prop] = tmpComponent[prop] || '';
                        }
                        items[prop].push(tmpItem);
                    }
                }
            }
            return items;
        },
        appliedFilters() {
            let filterOptions = this.filterOptions;
            let output = {};
            for (let i in filterOptions) {
                if (i === 'length') continue;
                if (filterOptions[i].type === 'date') {
                    let dateType = this.dateTypes[i];
                    let dateValues = this.dateValues;
                    if (!dateType) continue;
                    output[i] = {
                        dateType: dateType
                    };
                    if (i !== this.filterProp) {
                        // Get date when control is not available for date
                        output[i].date1 = dateValues[i].date1;
                        output[i].date2 = dateValues[i].date2;
                    }
                    if (isRelativeDate(dateType)) {
                        let dateOption = dateTypeOptions.find((date) => date.value === dateType);
                        output[i].date1 = dateOption.date1;
                        output[i].date2 = dateOption.date2;
                    }
                    else if (typeof this.controls['FILTER_DATE_INPUT__' + i] !== 'undefined') {
                        output[i].date1 = moment(this.controls['FILTER_DATE_INPUT__' + i].value).format('x');
                        if (this.controls['FILTER_DATE_INPUT2__' + i]) {
                            output[i].date2 = moment(this.controls['FILTER_DATE_INPUT2__' + i].value).format('x');
                        }
                    }
                }
                else {
                    if (typeof this.controls['FILTER_INPUT__' + i] !== 'undefined') {
                        output[i] = this.controls['FILTER_INPUT__' + i].value || [];
                    }
                }
            }
            return output;
        },
        fOptions() {
            let filterOptions = library.cloneObj(this.filterOptions);
            let appliedFilters = this.appliedFilters;
            // Only format filter options if the prop: filterOptions has changed
            if (
                library.isEqualObj(this.currentFilterOptions, filterOptions)
                && !this.updateOptions
            ) return this.formattedFilterOptions;
            this.updateOptions = false;
            this.currentFilterOptions = library.cloneObj(filterOptions);
            for (let i in filterOptions) {
                if (i === 'length') continue;
                if (filterOptions[i].type === 'date') {
                    if (!this.dateTypes[i]) {
                        this.dateTypes[i] = 'equal';
                    }
                    if (!this.dateValues[i]) {
                        this.dateValues[i] = {
                            date1: null,
                            date2: null
                        };
                    }
                }
                delete filterOptions[i].disabled;
                filterOptions[i].items = library.cloneObj(this.autocompleteItems[i]);
                let placeholder = filterOptions[i].placeholder ? filterOptions[i].placeholder : 'Filter by ' + filterOptions[i].label;
                filterOptions[i].placeholder = placeholder;
                let count = appliedFilters[i] && appliedFilters[i].length
                    ? appliedFilters[i].length
                    : 0;
                filterOptions[i].text = count + ' ' + filterOptions[i].label;
                filterOptions[i].key = i;
                filterOptions[i].allowCustomValue = true;
                filterOptions[i].allowFiltering = false;
                filterOptions[i].filterType = 'StartsWith';
                if (filterOptions[i].isGroupFor) {
                    filterOptions[i].groupBy = i;
                    if (typeof filterOptions[i].fields === 'undefined') {
                        filterOptions[i].fields = {
                            text: 'text',
                            value: 'value',
                            groupBy: i
                        };
                    } else {
                        filterOptions[i].fields.groupBy = i;
                    }
                }
                if (typeof this.additionalConfig === 'object') {
                    for (let j in this.additionalConfig) {
                        filterOptions[i][j] = this.additionalConfig[j];
                    }
                }
            }
            if (filterOptions && !this.selectedFilter) {
                let key = filterOptions[Object.keys(filterOptions)[0]].key;
                this.selectedFilter = key;
                this.filterProp = key;
            }
            this.filter();
            this.formattedFilterOptions = library.cloneObj(filterOptions);
            return filterOptions;
        }
    },
    methods: {
        clearAllFilters() {
            let filterOptions = library.cloneObj(this.filterOptions);
            for (let i in filterOptions) {
                if (i === 'length') continue;
                if (typeof this.controls['FILTER_INPUT__' + i] !== 'undefined') {
                    this.controls['FILTER_INPUT__' + i].value = [];
                }
                if (typeof this.controls['FILTER_DATE_INPUT__' + i] !== 'undefined') {
                    this.controls['FILTER_DATE_INPUT__' + i].value = '';
                }
                if (typeof this.controls['FILTER_DATE_INPUT2__' + i] !== 'undefined') {
                    this.controls['FILTER_DATE_INPUT2__' + i].value = '';
                }
                if (typeof this.controls['FILTER_DATE_TYPE'] !== 'undefined') {
                    this.controls['FILTER_DATE_TYPE'].value = 'equal';
                    this.dateTypes[i] = 'equal';
                }
            }
            this.filter();
        },
        switchType(type) {
            if (!type || !type.item) return;
            this.controls['FILTER_TYPE'].label = type.item.label || type.item.key;
            this.selectedFilter = type.item.key;
        },
        filter(event) {
            let filterFn = () => {
                let filteredItems = [];
                if (!this.filterItems || !this.filterItems.length) {
                    this.filteredItems = filteredItems;
                    return;
                }
                // Define function variables
                let tmpItems = library.cloneObj(this.filterItems);
                let filterBy = this.appliedFilters;
                let sKey = this.subObjectKey;
                // this.initControls();
                // Define filter functions
                const testAnyAllOption = (fKey, cKey) => {
                    if (!filterBy[fKey].length) return;
                    let cItem = sKey ? tmpItems[cKey][sKey] : tmpItems[cKey];
                    let match;
                    if (fKey === 'all') match = true;
                    else if (fKey === 'any') match = false;
                    // iterate through filter values
                    for (let idx in filterBy[fKey]) {
                        if (idx === 'length') continue;
                        let kVal = filterBy[fKey][idx].toLowerCase();
                        // Find a match for current filterKey
                        let excludedKeys = ['modifiedInstances'];
                        let fKeyMatch = false;
                        for (let key in cItem) {
                            if (key === 'length') continue;
                            let val = cItem[key];
                            if (
                                excludedKeys.indexOf(key) === -1
                                && typeof val === 'string'
                                && val.toLowerCase().indexOf(kVal) !== -1
                            ) {
                                fKeyMatch = true;
                            }
                        }
                        if (!fKeyMatch && fKey === 'all') {
                            match = !match;
                            break;
                        } else if (fKeyMatch && fKey === 'any') {
                            match = !match;
                            break;
                        }
                    }
                    if (!match) delete tmpItems[cKey];
                };
                const testFilterOption = (fKey, cKey) => {
                    let sKey = this.subObjectKey;
                    let tmpFkey = fKey;
                    if (this.filterOptions && this.filterOptions[fKey] && this.filterOptions[fKey].isGroupFor) {
                        tmpFkey = this.filterOptions[fKey].isGroupFor;
                    }
                    if (this.filterOptions && this.filterOptions[fKey] && typeof this.filterOptions[fKey].fields === 'object' && this.filterOptions[fKey].fields.value) {
                        tmpFkey = this.filterOptions[fKey].fields.value;
                    }
                    let kVal = sKey ? tmpItems[cKey][sKey][tmpFkey] : tmpItems[cKey][tmpFkey];
                    let exactMatch = !!(this.filterOptions && this.filterOptions[fKey] && this.filterOptions[fKey].exactMatch);
                    let compare;
                    let matched;
                    let queryType;
                    if (typeof kVal === 'undefined' || kVal === null) {
                        delete tmpItems[cKey];
                        return;
                    }
                    if (filterBy[fKey].dateType) {
                        // Handle Dates
                        let dateType = filterBy[fKey].dateType;
                        if (dateType) queryType = 'date';
                        if (dateType && isRelativeDate(filterBy[fKey].dateType)) {
                            let date1 = Number(filterBy[fKey].date1);
                            let date2 = Number(filterBy[fKey].date2);
                            let valueDate = library.convertDateToNumber(kVal);
                            matched = date1 <= valueDate && date2 >= valueDate;
                        }
                        else if (dateType) {
                            let date1 = Number(filterBy[fKey].date1);
                            let endOfDate1 = date1 + 86399; // seconds to the end of the day
                            let date2 = Number(filterBy[fKey].date2);
                            let valueDate = library.convertDateToNumber(kVal);
                            if (dateType === 'equal') matched = date1 === valueDate;
                            else if (dateType === 'before') matched = date1 > valueDate;
                            else if (dateType === 'after') matched = endOfDate1 < valueDate;
                            else if (dateType === 'between') matched = date1 <= valueDate && date2 >= valueDate;
                        }
                    }
                    else if (typeof kVal === 'string' && filterBy[fKey]) compare = new RegExp(library.escapeRegExp(filterBy[fKey].join('|')), 'ig');
                    else if (typeof kVal === 'number') compare = Number(filterBy[fKey]);
                    if (queryType === 'date') {
                        if (!matched && kVal !== compare) delete tmpItems[cKey];
                    } else if (exactMatch) {
                        if (filterBy[fKey].indexOf(kVal) === -1) delete tmpItems[cKey];
                    } else {
                        if ((typeof kVal === 'string' && kVal.search(compare) === -1)
                            || (typeof kVal === 'number' && kVal !== compare)
                        ) {
                            delete tmpItems[cKey];
                        }
                    }
                };
                const invalidOption = (filterOption) => (
                    (Array.isArray(filterOption) && !filterOption.length)
                    || typeof filterOption !== 'object'
                    || (filterOption.dateType
                        && (!library.isDate(filterOption.date1)
                            || (['equal', 'before', 'after'].indexOf(filterOption.dateType) === -1
                                && !library.isDate(filterOption.date2)
                            )
                        )
                    )
                );
                const applyFilterOptions = () => {
                    let sKey = this.subObjectKey;
                    for (let fKey in filterBy) {
                        if (fKey === 'length') continue;
                        // TODO maybe move this?
                        if (invalidOption(filterBy[fKey])) continue;
                        // end of new
                        for (let cKey in tmpItems) {
                            if (cKey === 'length') continue;
                            let kVal = sKey ? tmpItems[cKey][sKey] : tmpItems[cKey];
                            if (typeof kVal === 'undefined') continue;
                            if (fKey === 'all') testAnyAllOption(fKey, cKey);
                            else if (fKey === 'any') testAnyAllOption(fKey, cKey);
                            else testFilterOption(fKey, cKey);
                        }
                    }
                };
                // Apply filters
                if (this.filtersApplied) {
                    applyFilterOptions();
                }
                // debugger;
                filteredItems = Object.values(tmpItems);
                if (typeof this.additionalConfig === 'object' && this.additionalConfig.showResultsAfterFiltersApplied && !this.filtersApplied) {
                    filteredItems = [];
                }
                if (!library.isEqualObj(this.filteredItems, filteredItems)) {
                    this.filteredItems = filteredItems;
                    this.$emit('update:propResult', filteredItems);
                }
            };
            if (this.filterTimeout) clearTimeout(this.filterTimeout);
            this.filterTimeout = setTimeout(() => {
                this.firstFilterDelay = false;
                filterFn();
            }, this.firstFilterDelay || this.filterDelay);
        },
        filterDate(event) {
            // Don't automatically run filter for date ranges or user entered dates
            // Run it after they choose a date
            if (isRelativeDate(event.item.value) || this.filterItems.length === 0) {
                this.filter(event);
            }
        },
        filteringFnc(event, tmpElmnt) {
            if (
                tmpElmnt && tmpElmnt['index']
                && this.filterOptions
                && this.filterOptions[tmpElmnt['index']]
                && this.filterOptions[tmpElmnt['index']].isGroupFor) {
                let tmpSearchData = library.cloneObj(this.fOptions[tmpElmnt['index']].items);
                let tmpQuery = new Query();
                // frame the query based on search string with filter type.
                tmpQuery = (event.text !== "") ? tmpQuery.where(tmpElmnt['index'], "contains", event.text, true) : tmpQuery;
                // pass the filter data source, filter query to updateData method.
                event.updateData(tmpSearchData, tmpQuery);
            }
        },

        applyUnusedFilter: function () {
            if (this.controls['SWITCH_UNUSED_FILTER'] !== 'undefined' && typeof this.useModifyDateFilterCallback === 'function') {
                this.useModifyDateFilterCallback(this.controls['SWITCH_UNUSED_FILTER'].value);
            }
        }
    },
};

componentStore.initComponent(condensedFilterCmp, condensedFilterConfig);
export { condensedFilterCmp, condensedFilterConfig };
export default condensedFilterCmp;

</script>

<style scoped>
    .condensed-filter {
        padding: 5px;
        border: 1px solid #ccc;
        border-radius: 3px;
    }
    .filter-controls, .filter-status {
        display: flex;
    }
    .search-fields {
        flex-grow: 9;
        position: relative;
        margin-left: 1px;
    }
    .search-fields :deep(.e-multi-select-wrapper) {
        padding-left: 5px;
    }
    .search-fields :deep(.e-input-group), .e-input-group.e-control-wrapper {
        margin-bottom: 0;
    }
    .filter-controls :deep(.e-dropdown-btn) {
        padding-bottom: 7px;
        border-bottom: 1px solid #aaa;
        border-right: 1px dotted #aaa;
    }
    .filter-status {
        padding: 3px 0;
    }
    .filter-status .result-summary {
        padding-top: 3px;
        flex-grow: 9;
    }
    .filter-status :deep(.e-control.e-btn) {
        text-transform: lowercase;
        padding-top: 3px;
    }
    .filter-status-counter {
        display: inline-block; margin-top: 6px;
    }
    .filter-status-unused {
        float: right
    }
    .filter-status-unused-switch {
        display: inline-block; margin-left: 10px;
    }
    .filter-clear-all {
        margin-top: 5px;
    }
    .control-spacer {
        height: 30px;
        line-height: 30px;
    }
    .date-msg {
        padding-left: 5px;
        border-bottom: 1px solid #000;
    }
    .e-icons.e-search {
        position: absolute;
        right: 0;
        top: 0;
    }

</style>
